import React, { ReactNode } from "react";
import { Alert, Col, Container, Row, Spinner } from "react-bootstrap";
import styled from "styled-components";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

import { getDefaultDateRange, getWidgetTheme } from "../../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions, MetricConfig } from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import InteractiveLineChart from "../../../components/analytics/InteractiveLineChart";
import MetricsTable, { ColumnGroup, SortConfig } from "../../../components/analytics/MetricsTable";
import { getAnalyticsWidget } from "../../../utils/Requests";
import Logger from "../../../utils/Logger";

type RecsDashboardProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type RecsDashboardState = {
    isLoading: boolean;
    error: string | null;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    filterKey: number;
    recsRows: RecsRow[];
    recsDashRows: RecsApiRow[];
    recsDailyRows: RecsApiRow[];
    recsHourlyRows: RecsApiRow[];
    recsBrandBreakdownRows: RecsApiRow[];
    brandBreakdownSort: SortConfig;
    topBrandsVisibleSeries: Record<string, boolean>;
    hoveredTopBrandsSeries: string | null;
};

type RecsApiRow = Record<string, string | number | null | undefined>;

type RecsRow = {
    parameterRuleId: string;
    slotName: string;
    modelName: string;
    users: number;
    usersPercentChange: number | null;
    plays: number;
    playsPercentChange: number | null;
    playsPerUser: number | null;
    playsPerUserPercentChange: number | null;
    sessionConversionPct: number | null;
    sessionConversionPctChange: number | null;
    playedBrands: number;
    playedBrandsPercentChange: number | null;
    watchtime: number;
    watchtimePercentChange: number | null;
};

type RecsDashParsedRow = {
    brandRank: number;
    brandName: string;
    brandPlays: number;
    slotName: string;
    parameterRuleId: string;
    rulePlays: number;
    ruleSharePct: number | null;
};

type RecsDashChartPoint = {
    brandName: string;
    brandRank: number;
    brandPlays: number;
    [seriesKey: string]: string | number;
};

const STACK_COLORS: string[] = [
    "#4E79A7", // blue
    "#F28E2B", // orange
    "#59A14F", // green
    "#E15759", // red
    "#B07AA1", // purple
    "#76B7B2", // teal
    "#EDC948", // yellow
    "#FF9DA7", // pink
    "#9C755F", // brown
    "#BAB0AC", // gray
    "#2F4B7C", // deep blue
    "#A05195"  // magenta
];

const LINE_COLORS: string[] = [
    "#4E79A7",
    "#F28E2B",
    "#59A14F",
    "#E15759",
    "#B07AA1",
    "#76B7B2",
    "#EDC948",
    "#FF9DA7"
];

const RESP: any = ResponsiveContainer as any;
const CCHART: any = ComposedChart as any;
const BR: any = Bar as any;
const LN: any = Line as any;
const XA: any = XAxis as any;
const YA: any = YAxis as any;
const CG: any = CartesianGrid as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;

const MetricGrid = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: nowrap;
    overflow-x: auto;
`;

const MetricCard = styled.div`
    border: 1px solid #343a40;
    border-radius: 8px;
    padding: 16px;
    background: #212529;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    min-width: 220px;
`;

const MetricLabel = styled.div`
    font-size: 0.9rem;
    color: #f8f9fa;
    opacity: 0.7;
    margin-bottom: 8px;
`;

const MetricValue = styled.div`
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.1;
    color: #f8f9fa;
`;

const MetricChange = styled.div<{ $positive: boolean; $negative: boolean }>`
    margin-top: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${props => props.$positive ? "#28a745" : props.$negative ? "#dc3545" : "#6c757d"};
`;

const RuleSection = styled.div`
    margin-top: 24px;
`;

const RuleHeader = styled.h5`
    margin-bottom: 10px;
`;

class RecsDashboard extends React.Component<RecsDashboardProps, RecsDashboardState> {
    constructor(props: RecsDashboardProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();

        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            deviceType: []
        };

        this.state = {
            isLoading: true,
            error: null,
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandIds: [],
                deviceTypes: []
            },
            filterKey: 0,
            recsRows: [],
            recsDashRows: [],
            recsDailyRows: [],
            recsHourlyRows: [],
            recsBrandBreakdownRows: [],
            brandBreakdownSort: { key: "rec_plays", direction: "desc" },
            topBrandsVisibleSeries: {},
            hoveredTopBrandsSeries: null
        };
    }

    componentDidMount(): void {
        this.fetchData();
    }

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters },
            filterKey: prevState.filterKey + 1
        }), () => {
            this.fetchData();
        });
    };

    private toNumber(value: number | string | undefined | null): number | null {
        if (value === null || value === undefined || value === "") {
            return null;
        }

        if (typeof value === "number") {
            return Number.isFinite(value) ? value : null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private formatChange(changePercent: number | null): string {
        if (changePercent === null || changePercent === undefined) {
            return "No change data";
        }

        const sign = changePercent > 0 ? "+" : "";
        return `${sign}${changePercent.toFixed(2)}%`;
    }

    private fetchData = async (): Promise<void> => {
        const { appliedFilters } = this.state;

        try {
            this.setState({ isLoading: true, error: null });

            const [recsResponse, recsDashResponse, recsDailyResponse, recsHourlyResponse, recsBrandBreakdownResponse] = await Promise.all([
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "recs_plays",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.deviceType
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "recs_attributions",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.deviceType
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "recs_daily_counts",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.deviceType
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "recs_hourly_counts",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.deviceType
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "recs_brand_breakdown",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.deviceType
                )
            ]);

            const rowsRaw = (recsResponse as any)?.result;

            const recsRows: RecsRow[] = (Array.isArray(rowsRaw) ? rowsRaw : [])
                .map((row: RecsApiRow) => {
                    const plays = this.toNumber(row["plays"]) ?? 0;
                    const parameterRuleId = String(row["parameters_rule_id"] || row["parameter_rule_id"] || "Unknown");

                    return {
                        parameterRuleId,
                        slotName: String(row["slot_name"] || ""),
                        modelName: String(row["model_name"] || ""),
                        users: this.toNumber(row["users"]) ?? 0,
                        usersPercentChange: this.toNumber(row["users_percent_change"]),
                        plays,
                        playsPercentChange: this.toNumber(row["plays_percent_change"]),
                        playsPerUser: this.toNumber(row["plays_per_user"]),
                        playsPerUserPercentChange: this.toNumber(row["plays_per_user_percent_change"]),
                        sessionConversionPct: this.toNumber(row["session_conversion_pct"]),
                        sessionConversionPctChange: this.toNumber(row["session_conversion_pct_change"]),
                        playedBrands: this.toNumber(row["played_brands"]) ?? 0,
                        playedBrandsPercentChange: this.toNumber(row["played_brands_percent_change"]),
                        watchtime: this.toNumber(row["watchtime"]) ?? 0,
                        watchtimePercentChange: this.toNumber(row["watchtime_percent_change"])
                    };
                })
                .filter((row: RecsRow) => row.plays > 0)
                .sort((a: RecsRow, b: RecsRow) => b.plays - a.plays);

            const recsDashRaw = (recsDashResponse as any)?.result;
            const recsDashRows = Array.isArray(recsDashRaw) ? recsDashRaw as RecsApiRow[] : [];

            const recsDailyRaw = (recsDailyResponse as any)?.result;
            const recsDailyRows = Array.isArray(recsDailyRaw) ? recsDailyRaw as RecsApiRow[] : [];

            const recsHourlyRaw = (recsHourlyResponse as any)?.result;
            const recsHourlyRows = Array.isArray(recsHourlyRaw) ? recsHourlyRaw as RecsApiRow[] : [];

            const recsBrandBreakdownRaw = (recsBrandBreakdownResponse as any)?.result;
            const recsBrandBreakdownRows = Array.isArray(recsBrandBreakdownRaw) ? recsBrandBreakdownRaw as RecsApiRow[] : [];

            this.setState({ recsRows, recsDashRows, recsDailyRows, recsHourlyRows, recsBrandBreakdownRows });
        } catch (err) {
            Logger.error("Error fetching Recs dashboard data", err);
            this.setState({ error: "Failed to fetch Recs dashboard data" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private renderRecsMetrics(row: RecsRow): ReactNode {
        const playsPositive = (row.playsPercentChange ?? 0) > 0;
        const playsNegative = (row.playsPercentChange ?? 0) < 0;

        const usersPositive = (row.usersPercentChange ?? 0) > 0;
        const usersNegative = (row.usersPercentChange ?? 0) < 0;

        const conversionPositive = (row.sessionConversionPctChange ?? 0) > 0;
        const conversionNegative = (row.sessionConversionPctChange ?? 0) < 0;

        const brandsPositive = (row.playedBrandsPercentChange ?? 0) > 0;
        const brandsNegative = (row.playedBrandsPercentChange ?? 0) < 0;

        const playsPerUserPositive = (row.playsPerUserPercentChange ?? 0) > 0;
        const playsPerUserNegative = (row.playsPerUserPercentChange ?? 0) < 0;

        const watchtimePositive = (row.watchtimePercentChange ?? 0) > 0;
        const watchtimeNegative = (row.watchtimePercentChange ?? 0) < 0;
        const watchtimeHours = row.watchtime / 3600;

        return (
            <MetricGrid>
                <MetricCard>
                    <MetricLabel>Plays</MetricLabel>
                    <MetricValue>{row.plays.toLocaleString()}</MetricValue>
                    <MetricChange $positive={playsPositive} $negative={playsNegative}>
                        {this.formatChange(row.playsPercentChange)}
                    </MetricChange>
                </MetricCard>

                <MetricCard>
                    <MetricLabel>Users</MetricLabel>
                    <MetricValue>{row.users.toLocaleString()}</MetricValue>
                    <MetricChange $positive={usersPositive} $negative={usersNegative}>
                        {this.formatChange(row.usersPercentChange)}
                    </MetricChange>
                </MetricCard>

                <MetricCard>
                    <MetricLabel>Brands Played</MetricLabel>
                    <MetricValue>{row.playedBrands.toLocaleString()}</MetricValue>
                    <MetricChange $positive={brandsPositive} $negative={brandsNegative}>
                        {this.formatChange(row.playedBrandsPercentChange)}
                    </MetricChange>
                </MetricCard>

                <MetricCard>
                    <MetricLabel>Plays / user</MetricLabel>
                    <MetricValue>
                        {row.playsPerUser === null ? "-" : row.playsPerUser.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </MetricValue>
                    <MetricChange $positive={playsPerUserPositive} $negative={playsPerUserNegative}>
                        {this.formatChange(row.playsPerUserPercentChange)}
                    </MetricChange>
                </MetricCard>

                <MetricCard>
                    <MetricLabel>Conversion</MetricLabel>
                    <MetricValue>
                        {row.sessionConversionPct === null ? "-" : `${row.sessionConversionPct.toFixed(2)}%`}
                    </MetricValue>
                    <MetricChange $positive={conversionPositive} $negative={conversionNegative}>
                        {this.formatChange(row.sessionConversionPctChange)}
                    </MetricChange>
                </MetricCard>

                <MetricCard>
                    <MetricLabel>Watchtime (hours)</MetricLabel>
                    <MetricValue>{watchtimeHours.toLocaleString(undefined, { maximumFractionDigits: 2 })}</MetricValue>
                    <MetricChange $positive={watchtimePositive} $negative={watchtimeNegative}>
                        {this.formatChange(row.watchtimePercentChange)}
                    </MetricChange>
                </MetricCard>

            </MetricGrid>
        );
    }

    private parseRecsDashRows(): RecsDashParsedRow[] {
        const { recsDashRows } = this.state;

        return recsDashRows
            .map((row: RecsApiRow) => {
                const brandRank = this.toNumber(row["brand_rank"]) ?? 0;
                const brandName = String(row["brand_name"] || "Unknown");
                const brandPlays = this.toNumber(row["brand_plays"]) ?? 0;
                const slotName = String(row["slot_name"] || "").trim();
                const parameterRuleId = String(row["parameters_rule_id"] || "Unknown");
                const rulePlays = this.toNumber(row["rule_plays"]) ?? 0;
                const ruleSharePct = this.toNumber(row["rule_share_pct"]);

                return {
                    brandRank,
                    brandName,
                    brandPlays,
                    slotName,
                    parameterRuleId,
                    rulePlays,
                    ruleSharePct
                };
            })
            .filter((row: RecsDashParsedRow) => row.rulePlays > 0);
    }

    private getStackColor(index: number): string {
        return STACK_COLORS[index % STACK_COLORS.length];
    }

    private getRuleToSlotNameMap(): Record<string, string> {
        const ruleToSlot: Record<string, string> = {};
        this.state.recsRows.forEach((row: RecsRow) => {
            const slotName = (row.slotName || "").trim();
            if (slotName) {
                ruleToSlot[row.parameterRuleId] = slotName;
            }
        });
        return ruleToSlot;
    }

    private getRecsDashSeriesKey(row: RecsDashParsedRow, ruleToSlotName: Record<string, string>): string {
        return row.slotName || ruleToSlotName[row.parameterRuleId] || row.parameterRuleId;
    }

    private getRecsDashChartData(): { chartData: RecsDashChartPoint[]; seriesKeys: string[] } {
        const parsedRows = this.parseRecsDashRows();
        const ruleToSlotName = this.getRuleToSlotNameMap();
        const pointsByBrand = new Map<string, RecsDashChartPoint>();
        const seriesTotals: Record<string, number> = {};

        parsedRows.forEach((row: RecsDashParsedRow) => {
            const pointKey = `${row.brandRank}-${row.brandName}`;
            const existingPoint = pointsByBrand.get(pointKey);

            if (!existingPoint) {
                pointsByBrand.set(pointKey, {
                    brandName: row.brandName,
                    brandRank: row.brandRank,
                    brandPlays: row.brandPlays
                });
            } else if (!existingPoint.brandPlays && row.brandPlays > 0) {
                existingPoint.brandPlays = row.brandPlays;
            }

            // "All attributed" is an aggregate, not an individual stack segment.
            if (row.parameterRuleId === "All attributed") {
                return;
            }

            const seriesKey = this.getRecsDashSeriesKey(row, ruleToSlotName);
            const shareValue = row.ruleSharePct ?? 0;

            const point = pointsByBrand.get(pointKey);
            if (!point) return;

            const currentValue = typeof point[seriesKey] === "number" ? (point[seriesKey] as number) : 0;
            point[seriesKey] = currentValue + shareValue;
            seriesTotals[seriesKey] = (seriesTotals[seriesKey] || 0) + shareValue;
        });

        const seriesKeys = Object.keys(seriesTotals)
            .sort((a, b) => (seriesTotals[b] || 0) - (seriesTotals[a] || 0));

        const chartData = Array.from(pointsByBrand.values())
            .sort((a, b) => a.brandRank - b.brandRank);

        // Ensure all series keys exist on each point for stable stacked rendering.
        const normalizedChartData = chartData.map((point) => {
            const normalizedPoint: RecsDashChartPoint = { ...point };
            seriesKeys.forEach((seriesKey) => {
                if (typeof normalizedPoint[seriesKey] !== "number") {
                    normalizedPoint[seriesKey] = 0;
                }
            });
            return normalizedPoint;
        });

        return { chartData: normalizedChartData, seriesKeys };
    }

    private isTopBrandsSeriesVisible(seriesKey: string): boolean {
        const value = this.state.topBrandsVisibleSeries[seriesKey];
        return value === undefined ? true : value;
    }

    private handleTopBrandsSeriesToggle = (seriesKey: string): void => {
        this.setState((prevState) => ({
            topBrandsVisibleSeries: {
                ...prevState.topBrandsVisibleSeries,
                [seriesKey]: !this.isTopBrandsSeriesVisible(seriesKey)
            }
        }));
    };

    private handleTopBrandsToggleAll = (seriesKeys: string[]): void => {
        const allVisible = seriesKeys.every((key: string) => this.isTopBrandsSeriesVisible(key));
        const nextValue = !allVisible;
        const nextState: Record<string, boolean> = {};
        seriesKeys.forEach((key: string) => {
            nextState[key] = nextValue;
        });

        this.setState({ topBrandsVisibleSeries: nextState });
    };

    private handleTopBrandsSeriesMouseEnter = (seriesKey: string): void => {
        this.setState({ hoveredTopBrandsSeries: seriesKey });
    };

    private handleTopBrandsSeriesMouseLeave = (): void => {
        this.setState({ hoveredTopBrandsSeries: null });
    };

    private formatBrandAxisLabel(value: string): string {
        const label = String(value || "");
        const maxLength = 14;
        if (label.length <= maxLength) {
            return label;
        }
        return `${label.slice(0, maxLength - 1)}…`;
    }

    private getDailyCountsChartData(): { chartData: Array<Record<string, string | number>>; xKey: string; seriesKeys: string[] } {
        const { recsDailyRows } = this.state;
        if (!recsDailyRows.length) {
            return { chartData: [], xKey: "day", seriesKeys: [] };
        }

        const xKey = "day";
        const seriesTotals: Record<string, number> = {};
        const byDay = new Map<string, Record<string, string | number>>();

        recsDailyRows.forEach((row: RecsApiRow) => {
            const day = String(row["day"] ?? "").trim();
            if (!day) {
                return;
            }

            const slotName = String(row["slot_name"] || row["parameters_rule_id"] || "Unknown").trim();
            if (!slotName) {
                return;
            }

            const recPlays = this.toNumber(row["rec_plays"]) ?? 0;

            if (!byDay.has(day)) {
                byDay.set(day, { day });
            }

            const point = byDay.get(day);
            if (!point) {
                return;
            }

            const currentValue = typeof point[slotName] === "number" ? Number(point[slotName]) : 0;
            point[slotName] = currentValue + recPlays;
            seriesTotals[slotName] = (seriesTotals[slotName] || 0) + recPlays;
        });

        const seriesKeys = Object.keys(seriesTotals)
            .sort((a, b) => (seriesTotals[b] || 0) - (seriesTotals[a] || 0));

        const chartData = Array.from(byDay.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([, point]) => {
                const normalized: Record<string, string | number> = { ...point };
                seriesKeys.forEach((seriesKey) => {
                    if (typeof normalized[seriesKey] !== "number") {
                        normalized[seriesKey] = 0;
                    }
                });
                return normalized;
            });

        return { chartData, xKey, seriesKeys };
    }

    private getHourlyCountsChartData(): { chartData: Array<Record<string, string | number>>; xKey: string; seriesKeys: string[] } {
        const { recsHourlyRows } = this.state;
        if (!recsHourlyRows.length) {
            return { chartData: [], xKey: "hour_of_day", seriesKeys: [] };
        }

        const xKey = "hour_of_day";
        const seriesTotals: Record<string, number> = {};
        const byHour = new Map<number, Record<string, string | number>>();

        recsHourlyRows.forEach((row: RecsApiRow) => {
            const hourNumber = this.toNumber(row["hour_of_day"]);
            if (hourNumber === null) {
                return;
            }

            const slotName = String(row["slot_name"] || row["parameters_rule_id"] || "Unknown").trim();
            if (!slotName) {
                return;
            }

            const recPlays = this.toNumber(row["rec_plays"]) ?? 0;

            if (!byHour.has(hourNumber)) {
                byHour.set(hourNumber, { "hour_of_day": hourNumber });
            }

            const point = byHour.get(hourNumber);
            if (!point) {
                return;
            }

            const currentValue = typeof point[slotName] === "number" ? Number(point[slotName]) : 0;
            point[slotName] = currentValue + recPlays;
            seriesTotals[slotName] = (seriesTotals[slotName] || 0) + recPlays;
        });

        const seriesKeys = Object.keys(seriesTotals)
            .sort((a, b) => (seriesTotals[b] || 0) - (seriesTotals[a] || 0));

        const chartData = Array.from(byHour.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([, point]) => {
                const normalized: Record<string, string | number> = { ...point };
                seriesKeys.forEach((seriesKey) => {
                    if (typeof normalized[seriesKey] !== "number") {
                        normalized[seriesKey] = 0;
                    }
                });
                return normalized;
            });

        return { chartData, xKey, seriesKeys };
    }

    private formatHourLabel(value: string | number): string {
        const hour = typeof value === "number" ? value : Number(value);
        if (!Number.isFinite(hour)) {
            return String(value);
        }

        const normalized = Math.max(0, Math.min(23, Math.floor(hour)));
        const suffix = normalized < 12 ? "AM" : "PM";
        const twoDigitHour = String(normalized).padStart(2, "0");
        return `${twoDigitHour}:00${suffix}`;
    }

    private buildLineMetricConfig(seriesKeys: string[]): MetricConfig[] {
        return seriesKeys.map((key: string, index: number) => ({
            key,
            name: key,
            color: LINE_COLORS[index % LINE_COLORS.length]
        }));
    }

    private formatInteger(value: number | string | null | undefined): string {
        const parsed = this.toNumber(value);
        if (parsed === null) {
            return "-";
        }
        return Math.round(parsed).toLocaleString();
    }

    private formatPlayShare(value: number | string | null | undefined): string {
        const percentage = this.toNumber(value);
        if (percentage === null) {
            return "-";
        }

        return `${percentage.toFixed(1)}%`;
    }

    private isAllRecsSource(parameterRuleId: string): boolean {
        const normalized = parameterRuleId.trim().toLowerCase();
        return normalized === "" || normalized === "unknown" || normalized === "null" || normalized === "undefined";
    }

    private isUnknownSlotName(slotName: string): boolean {
        const normalized = slotName.trim().toLowerCase();
        return normalized === "" || normalized === "unknown" || normalized === "null" || normalized === "undefined";
    }

    private buildRuleHeader(row: RecsRow): string {
        const segments: string[] = [];

        if (!this.isUnknownSlotName(row.slotName)) {
            segments.push(`Slot: ${row.slotName}`);
        }

        if (this.isAllRecsSource(row.parameterRuleId)) {
            segments.push("Source: All recommendations");
        } else {
            segments.push(`Rule: ${row.parameterRuleId}`);
        }

        if (row.modelName) {
            segments.push(`Model: ${row.modelName}`);
        }

        return segments.join(" • ");
    }

    private getHeatmapCell(value: number | string | null | undefined, maxValue: number): ReactNode {
        const parsed = this.toNumber(value);
        const normalized = parsed === null || maxValue <= 0 ? 0 : Math.max(0, Math.min(1, parsed / maxValue));
        const alpha = 0.12 + (0.58 * normalized);

        return (
            <div
                style={{
                    background: `rgba(255, 72, 140, ${alpha})`,
                    borderRadius: "4px",
                    padding: "2px 6px",
                    fontWeight: 600,
                    color: "#f8f9fa"
                }}
            >
                {this.formatInteger(value)}
            </div>
        );
    }

    private handleBrandBreakdownSortChange = (key: string): void => {
        this.setState((prevState) => {
            if (prevState.brandBreakdownSort.key === key) {
                return {
                    brandBreakdownSort: {
                        key,
                        direction: prevState.brandBreakdownSort.direction === "asc" ? "desc" : "asc"
                    }
                };
            }

            return {
                brandBreakdownSort: {
                    key,
                    direction: key.includes("rank") ? "asc" : "desc"
                }
            };
        });
    };

    private getBrandBreakdownTableData(): Array<Record<string, string | number | null | undefined>> {
        const rows: Array<Record<string, string | number | null | undefined>> = this.state.recsBrandBreakdownRows.map((row: RecsApiRow, index: number) => ({
            ...row,
            brandDisplay: row["brand_name"] ?? row["brand"] ?? null,
            rowKey: `${String(row["brand_name"] ?? row["brand"] ?? "Unknown")}-${String(row["rec_plays_rank"] ?? "n/a")}-${index}`
        }));

        const { key, direction } = this.state.brandBreakdownSort;
        const multiplier = direction === "asc" ? 1 : -1;

        return rows.sort((a, b) => {
            const aNum = this.toNumber(a[key] as any);
            const bNum = this.toNumber(b[key] as any);

            if (aNum !== null && bNum !== null) {
                return (aNum - bNum) * multiplier;
            }

            const aString = String(a[key] ?? "").toLowerCase();
            const bString = String(b[key] ?? "").toLowerCase();
            return aString.localeCompare(bString) * multiplier;
        });
    }

    private getBrandBreakdownColumnGroups(maxRecPlays: number, maxOverallPlays: number): ColumnGroup[] {
        return [
            {
                name: "Brand",
                columns: [
                    {
                        key: "brandDisplay",
                        header: "Brand",
                        render: (value: string | null | undefined) => value ?? "-",
                        sortable: true
                    }
                ]
            },
            {
                name: "Rec Plays",
                columns: [
                    {
                        key: "rec_plays",
                        header: "Rec Plays",
                        render: (value: number | string | null | undefined) => this.getHeatmapCell(value, maxRecPlays),
                        sortable: true
                    },
                    {
                        key: "rec_plays_rank",
                        header: "Rec Plays Rank",
                        render: (value: number | string | null | undefined) => this.formatInteger(value),
                        sortable: true
                    }
                ]
            },
            {
                name: "Overall",
                columns: [
                    {
                        key: "overall_plays",
                        header: "Overall plays",
                        render: (value: number | string | null | undefined) => this.getHeatmapCell(value, maxOverallPlays),
                        sortable: true
                    },
                    {
                        key: "overall_rank",
                        header: "Overall Rank",
                        render: (value: number | string | null | undefined) => this.formatInteger(value),
                        sortable: true
                    }
                ]
            },
            {
                name: "Share",
                columns: [
                    {
                        key: "play_share",
                        header: "Play Share",
                        render: (value: number | string | null | undefined) => this.formatPlayShare(value),
                        sortable: true
                    }
                ]
            }
        ];
    }

    public render(): ReactNode {
        const {
            filters,
            filterOptions,
            isLoading,
            error,
            recsRows,
            recsDashRows,
            recsDailyRows,
            recsHourlyRows,
            brandBreakdownSort,
            hoveredTopBrandsSeries
        } = this.state;
        const { chartData, seriesKeys } = this.getRecsDashChartData();
        const {
            chartData: dailyChartData,
            xKey: dailyXAxisKey,
            seriesKeys: dailySeriesKeys
        } = this.getDailyCountsChartData();
        const {
            chartData: hourlyChartData,
            xKey: hourlyXAxisKey,
            seriesKeys: hourlySeriesKeys
        } = this.getHourlyCountsChartData();
        const chartTheme = getWidgetTheme(true);
        const dailyMetricConfig = this.buildLineMetricConfig(dailySeriesKeys);
        const hourlyMetricConfig = this.buildLineMetricConfig(hourlySeriesKeys);
        const brandBreakdownData = this.getBrandBreakdownTableData();
        const maxRecPlays = Math.max(0, ...brandBreakdownData.map((row) => this.toNumber(row.rec_plays as any) ?? 0));
        const maxOverallPlays = Math.max(0, ...brandBreakdownData.map((row) => this.toNumber(row.overall_plays as any) ?? 0));
        const brandBreakdownColumnGroups = this.getBrandBreakdownColumnGroups(maxRecPlays, maxOverallPlays);
        const visibleTopBrandSeriesKeys = seriesKeys.filter((key: string) => this.isTopBrandsSeriesVisible(key));
        const allTopBrandSeriesVisible = seriesKeys.length > 0 && seriesKeys.every((key: string) => this.isTopBrandsSeriesVisible(key));

        return (
            <div>
                <Container className="mw-100" key="recs-dashboard" style={{ position: "relative", paddingBottom: "40px" }}>
                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2>Recommendations Dashboard</h2>
                        </Col>
                    </Row>

                    <AnalyticsFiltersComponent
                        filters={filters}
                        filterOptions={filterOptions}
                        onFilterChange={this.handleFilterChange}
                        onApplyFilters={this.handleApplyFilters}
                        customerConfig={this.props.customerConfig}
                        enableGroupBy={{}}
                    />

                    {isLoading ? (
                        <Row className="justify-content-center">
                            <Col className="text-center" style={{ padding: "40px" }}>
                                <Spinner animation="border" variant="primary" />
                                <p style={{ marginTop: "10px" }}>Loading Recs dashboard...</p>
                            </Col>
                        </Row>
                    ) : error ? (
                        <Row>
                            <Col>
                                <Alert variant="danger">{error}</Alert>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            {recsRows.length === 0 ? (
                                <Row>
                                    <Col>
                                        <Alert variant="info">No rows with plays &gt; 0 for the selected date range.</Alert>
                                    </Col>
                                </Row>
                            ) : (
                                recsRows.map((row: RecsRow) => (
                                    <RuleSection key={`${row.parameterRuleId}-${row.slotName}-${row.modelName}`}>
                                        <RuleHeader>{this.buildRuleHeader(row)}</RuleHeader>
                                        {this.renderRecsMetrics(row)}
                                    </RuleSection>
                                ))
                            )}

                            <RuleSection>
                                <RuleHeader>Top brands - Play Share</RuleHeader>
                                {recsDashRows.length === 0 || chartData.length === 0 ? (
                                    <Alert variant="secondary" style={{ marginBottom: 0 }}>
                                        recs_attributions returned no stackable attribution rows for current filters.
                                    </Alert>
                                ) : visibleTopBrandSeriesKeys.length === 0 ? (
                                    <Alert variant="secondary" style={{ marginBottom: 0 }}>
                                        All slots are hidden. Use metric toggles to show at least one slot.
                                    </Alert>
                                ) : (
                                    <div style={{ border: "1px solid #343a40", borderRadius: "8px", background: "#212529", padding: "12px" }}>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                                marginBottom: "15px",
                                                flexWrap: "wrap",
                                                gap: "8px"
                                            }}
                                        >
                                            <div>
                                                <span style={{ fontWeight: "bold", color: "#f8f9fa" }}>Visible Metrics</span>
                                                <div style={{ fontSize: "11px", color: "#f8f9fa", opacity: 0.6, marginTop: "2px" }}>
                                                    Click to toggle • Hover to highlight
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                                                {seriesKeys.map((seriesKey: string, index: number) => {
                                                    const isVisible = this.isTopBrandsSeriesVisible(seriesKey);
                                                    const isHovered = hoveredTopBrandsSeries === seriesKey;
                                                    return (
                                                        <div
                                                            key={`top-brands-legend-${seriesKey}`}
                                                            onClick={() => this.handleTopBrandsSeriesToggle(seriesKey)}
                                                            onMouseEnter={() => this.handleTopBrandsSeriesMouseEnter(seriesKey)}
                                                            onMouseLeave={this.handleTopBrandsSeriesMouseLeave}
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: "4px",
                                                                padding: "4px 8px",
                                                                backgroundColor: isHovered ? "rgba(0,0,0,0.1)" : "#212529",
                                                                borderRadius: "4px",
                                                                border: "1px solid #343a40",
                                                                cursor: "pointer",
                                                                opacity: isVisible ? 1 : 0.4,
                                                                textDecoration: isVisible ? "none" : "line-through"
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    width: "12px",
                                                                    height: "12px",
                                                                    backgroundColor: this.getStackColor(index),
                                                                    borderRadius: "2px",
                                                                    opacity: isVisible ? 1 : 0.4
                                                                }}
                                                            />
                                                            <span
                                                                style={{
                                                                    fontSize: "12px",
                                                                    color: "#f8f9fa",
                                                                    fontWeight: isHovered ? "bold" : "normal"
                                                                }}
                                                            >
                                                                {seriesKey}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                                <button
                                                    type="button"
                                                    onClick={() => this.handleTopBrandsToggleAll(seriesKeys)}
                                                    style={{
                                                        padding: "4px 10px",
                                                        fontSize: "12px",
                                                        marginLeft: "4px",
                                                        border: "1px solid #6c757d",
                                                        color: "#f8f9fa",
                                                        background: "transparent",
                                                        borderRadius: "4px"
                                                    }}
                                                >
                                                    {allTopBrandSeriesVisible ? "Deselect All" : "Select All"}
                                                </button>
                                            </div>
                                        </div>
                                        <RESP width="100%" height={600}>
                                            <CCHART data={chartData} margin={{ top: 55, right: 30, left: 40, bottom: 10 }}>
                                                <CG strokeDasharray="3 3" stroke="#495057" />
                                                <XA
                                                    dataKey="brandName"
                                                    interval={2}
                                                    height={100}
                                                    tickFormatter={(value: any) => this.formatBrandAxisLabel(String(value))}
                                                    tick={{ 
                                                        angle: -45,
                                                        textAnchor: "end",
                                                        fill: "#f8f9fa", fontSize: 11 
                                                    }}
                                                    
                                                />
                                                <YA
                                                    yAxisId="share"
                                                    tick={{ fill: "#f8f9fa", fontSize: 12 }}
                                                    tickFormatter={(value: number) => `${value.toFixed(1)}%`}
                                                    domain={[0, 100]}
                                                />
                                                <YA
                                                    yAxisId="plays"
                                                    orientation="right"
                                                    tick={{ fill: "#f8f9fa", fontSize: 12 }}
                                                    tickFormatter={(value: number) => value.toLocaleString()}
                                                />
                                                <TT
                                                    formatter={(value: number, name: string) => {
                                                        if (name === "Brand plays") {
                                                            return [typeof value === "number" ? value.toLocaleString() : value, name];
                                                        }

                                                        return [`${Number(value).toFixed(2)}%`, name];
                                                    }}
                                                    contentStyle={{ backgroundColor: "#1b1f23", border: "1px solid #343a40", color: "#f8f9fa" }}
                                                    itemStyle={{ color: "#f8f9fa" }}
                                                    labelStyle={{ color: "#f8f9fa" }}
                                                />
                                                <LG wrapperStyle={{ display: "none" }} />
                                                {visibleTopBrandSeriesKeys.map((seriesKey: string) => {
                                                    const originalIndex = seriesKeys.indexOf(seriesKey);
                                                    const isHovered = hoveredTopBrandsSeries === seriesKey;
                                                    const hasHover = hoveredTopBrandsSeries !== null;
                                                    const fillOpacity = hasHover && !isHovered ? 0.3 : 1;

                                                    return (
                                                    <BR
                                                        key={seriesKey}
                                                        yAxisId="share"
                                                        stackId="slotShare"
                                                        dataKey={seriesKey}
                                                        fill={this.getStackColor(originalIndex)}
                                                        fillOpacity={fillOpacity}
                                                        name={seriesKey}
                                                    />
                                                    );
                                                })}
                                                <LN
                                                    yAxisId="plays"
                                                    type="monotone"
                                                    dataKey="brandPlays"
                                                    stroke="#ff8c42"
                                                    strokeWidth={2}
                                                    dot={{ r: 2 }}
                                                    name="Brand plays"
                                                />
                                            </CCHART>
                                        </RESP>
                                    </div>
                                )}
                            </RuleSection>

                            <RuleSection>
                                <RuleHeader>Recommended Plays</RuleHeader>
                                {recsDailyRows.length === 0 || dailyChartData.length === 0 || dailySeriesKeys.length === 0 ? (
                                    <Alert variant="secondary" style={{ marginBottom: 0 }}>
                                        recs_daily_counts returned no chartable rows for current filters.
                                    </Alert>
                                ) : (
                                    <InteractiveLineChart
                                        title=""
                                        data={dailyChartData}
                                        xAxisDataKey={dailyXAxisKey}
                                        metrics={dailyMetricConfig}
                                        theme={chartTheme}
                                        height={380}
                                        tooltipValueFormatter={(value: number) => value.toLocaleString()}
                                    />
                                )}
                            </RuleSection>

                            <RuleSection>
                                <RuleHeader>Recommended Plays by Hour</RuleHeader>
                                {recsHourlyRows.length === 0 || hourlyChartData.length === 0 || hourlySeriesKeys.length === 0 ? (
                                    <Alert variant="secondary" style={{ marginBottom: 0 }}>
                                        recs_hourly_counts returned no chartable rows for current filters.
                                    </Alert>
                                ) : (
                                    <InteractiveLineChart
                                        title=""
                                        data={hourlyChartData}
                                        xAxisDataKey={hourlyXAxisKey}
                                        metrics={hourlyMetricConfig}
                                        theme={chartTheme}
                                        height={380}
                                        xAxisTickFormatter={(value: string) => this.formatHourLabel(value)}
                                        tooltipLabelFormatter={(value: string) => this.formatHourLabel(value)}
                                        tooltipValueFormatter={(value: number) => value.toLocaleString()}
                                    />
                                )}
                            </RuleSection>

                            <RuleSection>
                                <RuleHeader>Brand Breakdown</RuleHeader>
                                {brandBreakdownData.length === 0 ? (
                                    <Alert variant="secondary" style={{ marginBottom: 0 }}>
                                        recs_brand_breakdown returned no rows for current filters.
                                    </Alert>
                                ) : (
                                    <MetricsTable
                                        data={brandBreakdownData}
                                        columnGroups={brandBreakdownColumnGroups}
                                        rowKeyField="rowKey"
                                        emptyMessage="No brand breakdown data available"
                                        sortConfig={brandBreakdownSort}
                                        onSortChange={this.handleBrandBreakdownSortChange}
                                    />
                                )}
                            </RuleSection>
                        </>
                    )}
                </Container>
            </div>
        );
    }
}

export default RecsDashboard;